import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Container } from '@components/atoms/Container';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { useThemes } from '@hooks/useThemes';
import { useData } from '@hooks/useData';
import { useGetAllPaymentMethods } from '@services/useGetAllPaymentMethods';
import { SITE_URLS } from '@constants';
import { FinancingLogo } from '../FinancingLogo';
import livroReclamacoesLogo from '@assets/images/logos/livro-reclamacoes.svg';
import styles from './Footer.module.css';
import { Modules, useModule } from '@hooks/useModule';
import { useSettings } from '@hooks/useSettings';
import { MunicipalityLogo } from '../MunicipalityLogo';
import { useLocation } from 'react-router';
import { isPtMobilePhone } from '@utils/helper';

export interface FooterProps {
    mode: 'short' | 'normal';
}

export const Footer: FC<FooterProps> = ({ mode }) => {
    const { isModuleActive } = useModule();
    const { allSettings } = useData();
    const { useGrayscaleTheme } = useThemes();
    const location = useLocation();
    const { t } = useTranslation();
    const { getPage } = useSettings();

    const translations = {
        allRightsReserved: t('footer-component.all-rights-reserved'),
        livroReclamacoesAlt: t('footer-component.livro-reclamacoes.alt'),
        ptMobilePhoneNetworkDisclaimer: t('pt-mobile-phone-network-disclaimer'),
        refunds: t('policy-component.refunds.title'),
        termsConditions: t('policy-component.terms-and-conditions.title'),
        privacy: t('policy-component.privacy-policy.title'),
        cookies: t('policy-component.cookies-policy.title'),
        points: t('footer-component.points'),
    };

    // Data from API
    const data = allSettings?.data;

    const logoFooterItem = data?.layouts?.find((item) => item.name === 'logoFooter');
    const logoFooterSrc = logoFooterItem?.value;

    const hasPolicyPageContent = (pageKey: string): boolean => {
        const page = getPage(pageKey);
        if (!page) {
            return false;
        }

        if (typeof page === 'string' && page.trim() !== '') {
            return true;
        }

        return false;
    };

    const isCurrentPage = (pageKey: string) => {
        return location.pathname === '/' + SITE_URLS.policy.replace(':pageKey', pageKey);
    };

    const showLocalCallNote: boolean = data?.phone && isPtMobilePhone(data.phone) ? true : false;

    const [canAccessPointsPage, setCanAccessPointsPage] = useState<boolean | null>(null);

    // if gamification module is not active, user can't access this page
    useEffect(() => {
        if (allSettings !== null) {
            const pointsActive = isModuleActive(Modules.pointsGamification);
            pointsActive !== canAccessPointsPage && setCanAccessPointsPage(pointsActive);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings]);

    return (
        <footer
            className={
                // themes
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ` ${styles.footer}`
            }
            data-testid="footer">
            {mode === 'short' ? (
                <>
                    <div className="container-fluid">
                        <hr className="m-0" />
                    </div>
                    <Container classes="py-4 py-lg-3">
                        <div className="d-block d-lg-flex justify-content-between text-center">
                            <div className="align-self-center py-4 py-lg-3">
                                <Typography classes="p-0 m-0 text-body-secondary">
                                    <>
                                        {data?.copyright && (
                                            <>
                                                {data?.copyright}
                                                <br />
                                            </>
                                        )}

                                        {translations.allRightsReserved}
                                    </>
                                </Typography>
                            </div>
                            <FinancingLogo />
                            <div className="d-inline-block align-self-center d-lg-block py-4 py-lg-3 pe-3 pe-lg-0">
                                {/* #TODO: confirm URL */}
                                <a
                                    href="https://www.consumidor.gov.pt/livro-de-reclamacoes.aspx"
                                    target="_blank"
                                    rel="noreferrer">
                                    <img className="mx-2" src={livroReclamacoesLogo} alt={translations.livroReclamacoesAlt} />
                                </a>
                            </div>
                            <PaymentMethods />
                        </div>
                    </Container>
                </>
            ) : (
                <>
                    <Container classes="pt-5 pb-4 py-md-5">
                        <div className="d-block d-md-flex justify-content-between py-0 py-md-5 text-center">
                            <div className="d-flex flex-column my-3">
                                <img
                                    className={`${styles['brand-logo']} mt-2 mb-5`}
                                    src={logoFooterSrc}
                                    alt={allSettings?.data?.marketplaceName}
                                />
                                {data?.social_networks && (
                                    <ul className="nav d-inline-flex justify-content-center">
                                        {data.social_networks.map(
                                            (socialNetwork, index) =>
                                                socialNetwork.url && (
                                                    <li className="mb-2 px-2" key={index}>
                                                        <Anchor
                                                            href={socialNetwork.url}
                                                            mode={AnchorMode.none}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            ariaLabel={socialNetwork.name}>
                                                            <RoundIcon
                                                                classes={`${styles['footer-icons']}`}
                                                                icon={socialNetwork.icon}
                                                                variant={RoundIconVariants.variant1}
                                                            />
                                                        </Anchor>
                                                    </li>
                                                )
                                        )}
                                    </ul>
                                )}
                            </div>
                            <div className="my-3">
                                <ul className="nav flex-column">
                                    {data?.location && (
                                        <li className="my-2 d-none d-md-block">
                                            <Typography
                                                tagVariant={TypographyTagVariants.h3}
                                                classes="p-0 m-0 text-body-secondary"
                                                fontSize="14">
                                                <>
                                                    {data?.location}
                                                    <br />
                                                    {data?.locationComplement}
                                                </>
                                            </Typography>
                                        </li>
                                    )}
                                    {data?.email && (
                                        <li className="my-2">
                                            <Typography
                                                tagVariant={TypographyTagVariants.h3}
                                                classes="p-0 m-0 text-body-secondary"
                                                fontSize="14">
                                                {data?.email ? (
                                                    <Anchor mailTo={data?.email} mode={AnchorMode.none}>
                                                        {data?.email}
                                                    </Anchor>
                                                ) : (
                                                    <span></span>
                                                )}
                                            </Typography>
                                        </li>
                                    )}
                                    {data?.phone && (
                                        <li className="my-2">
                                            <Typography
                                                tagVariant={TypographyTagVariants.h3}
                                                classes="p-0 m-0 text-body-secondary"
                                                fontSize="14">
                                                <>
                                                    {data?.phone}
                                                    {showLocalCallNote && (
                                                        <>
                                                            <br />
                                                            <Typography
                                                                classes="d-none d-md-block"
                                                                tagVariant={TypographyTagVariants.span}
                                                                fontSize="12">
                                                                {translations.ptMobilePhoneNetworkDisclaimer}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </>
                                            </Typography>
                                        </li>
                                    )}
                                </ul>
                            </div>
                            {hasPolicyPageContent('refunds') ||
                            canAccessPointsPage ||
                            hasPolicyPageContent('terms-and-conditions') ||
                            hasPolicyPageContent('cookies-policy') ||
                            hasPolicyPageContent('privacy-policy') ? (
                                <div className="my-3">
                                    <ul className="nav flex-column">
                                        {hasPolicyPageContent('refunds') && (
                                            <li className="my-2">
                                                <Typography tagVariant={TypographyTagVariants.h3} fontSize="14">
                                                    <Anchor
                                                        classes={isCurrentPage('refunds') ? styles['footer-subtitles'] : ''}
                                                        href={SITE_URLS.policy.replace(':pageKey', 'refunds')}
                                                        mode={AnchorMode.none}>
                                                        {translations.refunds}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}

                                        {canAccessPointsPage && (
                                            <li className="my-2">
                                                <Typography tagVariant={TypographyTagVariants.h3} fontSize="14">
                                                    <Anchor
                                                        classes={
                                                            location.pathname === '/' + SITE_URLS.howToUsePoints
                                                                ? styles['footer-subtitles']
                                                                : ''
                                                        }
                                                        href={SITE_URLS.howToUsePoints}
                                                        mode={AnchorMode.none}>
                                                        {translations.points}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}

                                        {hasPolicyPageContent('terms-and-conditions') && (
                                            <li className="my-2">
                                                <Typography tagVariant={TypographyTagVariants.h3} fontSize="14">
                                                    <Anchor
                                                        classes={
                                                            isCurrentPage('terms-and-conditions')
                                                                ? styles['footer-subtitles']
                                                                : ''
                                                        }
                                                        href={SITE_URLS.policy.replace(':pageKey', 'terms-and-conditions')}
                                                        mode={AnchorMode.none}>
                                                        {translations.termsConditions}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}
                                        {hasPolicyPageContent('privacy-policy') && (
                                            <li className="my-2">
                                                <Typography tagVariant={TypographyTagVariants.h3} fontSize="14">
                                                    <Anchor
                                                        classes={
                                                            isCurrentPage('privacy-policy') ? styles['footer-subtitles'] : ''
                                                        }
                                                        href={SITE_URLS.policy.replace(':pageKey', 'privacy-policy')}
                                                        mode={AnchorMode.none}>
                                                        {translations.privacy}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}
                                        {hasPolicyPageContent('cookies-policy') && (
                                            <li className="my-2">
                                                <Typography tagVariant={TypographyTagVariants.h3} fontSize="14">
                                                    <Anchor
                                                        classes={
                                                            isCurrentPage('cookies-policy') ? styles['footer-subtitles'] : ''
                                                        }
                                                        href={SITE_URLS.policy.replace(':pageKey', 'cookies-policy')}
                                                        mode={AnchorMode.none}>
                                                        {translations.cookies}
                                                    </Anchor>
                                                </Typography>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            ) : (
                                ''
                            )}
                        </div>
                    </Container>
                    <div className="container-fluid">
                        <hr className="m-0" />
                    </div>
                    <Container classes="py-4 py-lg-3">
                        <div className="d-block d-lg-flex justify-content-between text-center">
                            <div className="align-self-center py-4 py-lg-3">
                                <Typography classes="p-0 m-0 text-body-secondary" fontSize="14">
                                    <>
                                        {data?.copyright && (
                                            <>
                                                {data?.copyright}
                                                <br />
                                            </>
                                        )}
                                        {translations.allRightsReserved}
                                    </>
                                </Typography>
                            </div>
                            <PaymentMethods />
                            <div className="d-inline-block align-self-center d-lg-block py-4 py-lg-3 pe-3 pe-lg-0">
                                {/* #TODO: confirm URL */}
                                <a
                                    href="https://www.consumidor.gov.pt/livro-de-reclamacoes.aspx"
                                    target="_blank"
                                    rel="noreferrer">
                                    <img className="mx-2" src={livroReclamacoesLogo} alt={translations.livroReclamacoesAlt} />
                                </a>
                            </div>
                        </div>
                    </Container>
                    <div className="container-fluid">
                        <hr className="m-0" />
                    </div>
                    <Container classes="py-4 py-lg-3">
                        <div className={`${styles.financingLogosWrapper} d-block d-lg-flex justify-content-between text-center`}>
                            <div className={styles.financingLogoLeft}>
                                <MunicipalityLogo />
                            </div>
                            <div className={styles.financingLogoRight}>
                                <FinancingLogo />
                            </div>
                        </div>
                    </Container>
                </>
            )}
        </footer>
    );
};

const PaymentMethods: FC = () => {
    const { allPaymentMethods } = useData();
    const { request: requestAllPaymentMethods } = useGetAllPaymentMethods();
    const { isModuleActive } = useModule();

    const displayPaymentMethods = !isModuleActive(Modules.catalogMode);

    // call services, if we don't have the information yet
    useEffect(() => {
        displayPaymentMethods && !allPaymentMethods && requestAllPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allPaymentMethods]);

    return (
        <>
            {displayPaymentMethods && (
                <div className="d-inline-block align-self-center d-lg-block py-4 py-lg-3">
                    {allPaymentMethods?.data?.map(
                        (item, index) =>
                            item.image?.src && (
                                <img key={`payment-method-${index}`} className="px-1" src={item.image.src} alt={item.name} />
                            )
                    )}
                </div>
            )}
        </>
    );
};
